import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Drawer,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import VpnKeyRoundedIcon from '@mui/icons-material/VpnKeyRounded';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import { useOrganizationSelect } from '../../../context/OrganizationSelectContext';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const INITIAL_FORM = {
  organizationId: '',
  apiKey: '',
  apiUrl: 'https://api.signitic.app',
  enabled: true,
};

function getStatusMeta(status) {
  if (status === 'success') {
    return {
      label: 'Synchro OK',
      color: 'success',
      icon: <CheckCircleRoundedIcon fontSize="small" />,
    };
  }

  if (status === 'partial') {
    return {
      label: 'Partielle',
      color: 'warning',
      icon: <WarningAmberRoundedIcon fontSize="small" />,
    };
  }

  if (status === 'error') {
    return {
      label: 'Erreur',
      color: 'error',
      icon: <ErrorOutlineRoundedIcon fontSize="small" />,
    };
  }

  return {
    label: 'Jamais synchronise',
    color: 'default',
    icon: <WarningAmberRoundedIcon fontSize="small" />,
  };
}

function formatDate(value) {
  if (!value) return 'Jamais';

  return new Date(value).toLocaleString('fr-FR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function MetricCard({ title, value, subtitle, icon, tone = 'primary' }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.25,
        borderRadius: 3,
        border: '1px solid',
        borderColor: `${tone}.light`,
        background: `linear-gradient(135deg, ${tone === 'primary' ? '#eef4ff' : tone === 'success' ? '#edf9f0' : tone === 'warning' ? '#fff7e8' : '#f5f7fb'} 0%, #ffffff 100%)`,
        height: '100%',
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 2.5,
            display: 'grid',
            placeItems: 'center',
            bgcolor: `${tone}.main`,
            color: '#fff',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
      </Stack>

      <Typography variant="h4" fontWeight={700} lineHeight={1}>
        {value}
      </Typography>

      <Typography variant="body2" color="text.secondary" mt={0.75}>
        {subtitle}
      </Typography>
    </Paper>
  );
}

function Signitic() {
  const { selectedOrganization } = useOrganizationSelect();

  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingConfigs, setLoadingConfigs] = useState(false);
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncingConfigId, setSyncingConfigId] = useState('');
  const [saving, setSaving] = useState(false);

  const [signiticUsers, setSigniticUsers] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [selectedConfigId, setSelectedConfigId] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [feedback, setFeedback] = useState({ type: 'info', message: '' });
  const [configDialogOpen, setConfigDialogOpen] = useState(false);

  const setMessage = (type, message) => setFeedback({ type, message });

  const fetchOrganizations = async () => {
    try {
      setLoadingOrganizations(true);
      const { data } = await api.get('/api/organizations/list');
      setOrganizations(Array.isArray(data) ? data : []);
    } catch (error) {
      setMessage('error', error?.response?.data?.error || 'Impossible de charger les organisations.');
    } finally {
      setLoadingOrganizations(false);
    }
  };

  const fetchConfigs = async () => {
    try {
      setLoadingConfigs(true);
      const { data } = await api.get('/api/signitic/configs');
      setConfigs(Array.isArray(data) ? data : []);
    } catch (error) {
      setMessage('error', error?.response?.data?.error || 'Impossible de charger les configurations Signitic.');
    } finally {
      setLoadingConfigs(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const { data } = await api.get('/api/signitic/users');
      const rows = Array.isArray(data) ? data : [];
      const filteredRows = selectedOrganization
        ? rows.filter((item) => item?.organization?.name === selectedOrganization)
        : rows;
      setSigniticUsers(filteredRows);
    } catch (error) {
      setMessage('error', error?.response?.data?.error || 'Impossible de charger les utilisateurs Signitic.');
    } finally {
      setLoadingUsers(false);
    }
  };

  const hydrate = async () => {
    await Promise.all([fetchOrganizations(), fetchConfigs(), fetchUsers()]);
  };

  useEffect(() => {
    hydrate();
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [selectedOrganization]);

  const handleChange = (field) => (event) => {
    const value = field === 'enabled' ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setSelectedConfigId(null);
    setForm(INITIAL_FORM);
  };

  const openConfigDialog = () => {
    setConfigDialogOpen(true);
  };

  const closeConfigDialog = () => {
    setConfigDialogOpen(false);
  };

  const handleEditConfig = (config) => {
    setSelectedConfigId(config._id);
    setForm({
      organizationId: config?.Organization?._id || '',
      apiKey: '',
      apiUrl: config?.apiUrl || 'https://api.signitic.app',
      enabled: Boolean(config?.enabled),
    });
    setMessage('info', `Modification de la configuration pour ${config?.Organization?.name || 'organisation inconnue'}. Renseigne une nouvelle cle API pour enregistrer.`);
  };

  const handleSaveConfig = async () => {
    if (!form.organizationId || !form.apiKey) {
      setMessage('warning', 'Selectionne une organisation et renseigne une cle API.');
      return;
    }

    try {
      setSaving(true);
      await api.post('/api/signitic/configs', form);
      setMessage('success', selectedConfigId ? 'Configuration mise a jour.' : 'Configuration creee.');
      resetForm();
      await Promise.all([fetchConfigs(), fetchUsers()]);
    } catch (error) {
      setMessage('error', error?.response?.data?.error || 'Impossible d enregistrer la configuration.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfig = async (configId) => {
    try {
      await api.delete(`/api/signitic/configs/${configId}`);
      if (selectedConfigId === configId) {
        resetForm();
      }
      setMessage('success', 'Configuration desactivee.');
      await Promise.all([fetchConfigs(), fetchUsers()]);
    } catch (error) {
      setMessage('error', error?.response?.data?.error || 'Impossible de desactiver la configuration.');
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      const { data, status } = await api.get('/api/signitic/usersDB');
      const hasPartial = status === 207 || data?.results?.some((item) => item.failed > 0);
      setMessage(
        hasPartial ? 'warning' : 'success',
        hasPartial
          ? 'Synchro terminee avec quelques erreurs. Consulte le detail des configurations.'
          : 'Synchro Signitic terminee avec succes.'
      );
      await Promise.all([fetchConfigs(), fetchUsers()]);
    } catch (error) {
      setMessage('error', error?.response?.data?.error || 'La synchronisation Signitic a echoue.');
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncConfig = async (config) => {
    try {
      setSyncingConfigId(config._id);
      const { data, status } = await api.post(`/api/signitic/configs/${config._id}/sync`);
      const hasPartial = status === 207 || (data?.failed || 0) > 0;
      setMessage(
        hasPartial ? 'warning' : 'success',
        hasPartial
          ? `Synchro partielle pour ${config?.Organization?.name || 'l organisation selectionnee'}.`
          : `Synchro terminee pour ${config?.Organization?.name || 'l organisation selectionnee'}.`
      );
      await Promise.all([fetchConfigs(), fetchUsers()]);
    } catch (error) {
      setMessage('error', error?.response?.data?.error || 'La synchronisation de cette organisation a echoue.');
    } finally {
      setSyncingConfigId('');
    }
  };

  const selectedOrgName = useMemo(() => {
    if (!selectedOrganization) return 'Toutes les organisations';
    return selectedOrganization;
  }, [selectedOrganization]);

  const metrics = useMemo(() => {
    const activeConfigs = configs.filter((item) => item.enabled && !item.deleted).length;
    const activeUsers = signiticUsers.filter((item) => item.status).length;
    const configsInError = configs.filter((item) => item.lastSyncStatus === 'error' || item.lastSyncStatus === 'partial').length;

    return {
      configCount: configs.length,
      activeConfigs,
      userCount: signiticUsers.length,
      activeUsers,
      configsInError,
    };
  }, [configs, signiticUsers]);

  const userColumns = useMemo(
    () => [
      {
        header: 'Utilisateur',
        columns: [
          {
            accessorKey: 'firstname',
            header: 'Prenom',
            size: 110,
            filterVariant: 'autocomplete',
            filterFn: 'equals',
          },
          {
            accessorKey: 'lastname',
            header: 'Nom',
            size: 120,
            filterVariant: 'autocomplete',
            filterFn: 'equals',
          },
          {
            accessorKey: 'email',
            header: 'Email',
            size: 220,
            filterVariant: 'autocomplete',
            filterFn: 'equals',
          },
        ],
      },
      {
        header: 'Contexte',
        columns: [
          {
            accessorFn: (row) => row?.organization?.name || '',
            id: 'organizationName',
            header: 'Client',
            size: 170,
            filterVariant: 'autocomplete',
            filterFn: 'equals',
          },
          {
            accessorKey: 'signiticGroup',
            header: 'Groupe Signitic',
            size: 180,
            filterVariant: 'autocomplete',
            filterFn: 'equals',
          },
          {
            accessorFn: (row) => (row.status ? 'Actif' : 'Inactif'),
            id: 'statusLabel',
            header: 'Statut',
            size: 110,
            filterVariant: 'autocomplete',
            filterFn: 'equals',
            Cell: ({ row }) => (
              row.original.status
                ? <Chip label="Actif" color="success" size="small" variant="outlined" />
                : <Chip label="Inactif" color="default" size="small" variant="outlined" />
            ),
          },
          {
            accessorFn: (row) => row?.config?.lastSyncStatus || '',
            id: 'configSyncStatus',
            header: 'Etat config',
            size: 130,
            filterVariant: 'autocomplete',
            Cell: ({ row }) => {
              const meta = getStatusMeta(row?.original?.config?.lastSyncStatus);
              return <Chip size="small" color={meta.color} label={meta.label} variant="outlined" />;
            },
          },
          {
            accessorFn: (row) => formatDate(row?.lastSyncedAt),
            id: 'lastSyncedAt',
            header: 'Derniere synchro',
            size: 170,
          },
        ],
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns: userColumns,
    data: signiticUsers,
    state: {
      isLoading: loadingUsers,
    },
    enableColumnFilterModes: true,
    enableColumnOrdering: true,
    enableGrouping: true,
    enableColumnPinning: true,
    enableFacetedValues: true,
    initialState: {
      density: 'compact',
      showColumnFilters: true,
      columnPinning: {
        left: ['mrt-row-expand'],
      },
      grouping: ['organizationName'],
      expanded: {},
      columnFilters: [
        {
          id: 'statusLabel',
          value: 'Actif',
        },
      ],
      pagination: {
        pageSize: 50,
      },
      sorting: [
        { id: 'organizationName', desc: false },
        { id: 'lastname', desc: false },
      ],
    },
    muiSearchTextFieldProps: {
      size: 'small',
      placeholder: 'Rechercher un utilisateur Signitic',
      variant: 'outlined',
    },
    muiPaginationProps: {
      color: 'primary',
      rowsPerPageOptions: [25, 50, 100],
      shape: 'rounded',
      variant: 'outlined',
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        borderRadius: 4,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
      },
    },
    renderTopToolbarCustomActions: () => (
      <Stack direction="row" spacing={1} alignItems="center">
        <Chip
          size="small"
          color="primary"
          variant="outlined"
          label={selectedOrgName}
        />
        <Chip
          size="small"
          color="default"
          variant="outlined"
          label={`${signiticUsers.length} utilisateur${signiticUsers.length > 1 ? 's' : ''}`}
        />
      </Stack>
    ),
  });

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Card
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 5,
          color: '#132238',
          background: 'linear-gradient(135deg, #f3f7ff 0%, #ffffff 45%, #eefaf6 100%)',
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            spacing={2}
            alignItems={{ xs: 'flex-start', md: 'center' }}
          >
            <Box>
              <Typography variant="overline" sx={{ letterSpacing: 1.4, color: 'text.secondary' }}>
                Signitic Control Room
              </Typography>
              <Typography variant="h4" fontWeight={800} sx={{ mb: 1 }}>
                Signatures et configurations par organisation
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 720 }}>
                Gere les cles API Signitic par client, lance la synchronisation globale et consulte les utilisateurs remontes depuis chaque organisation.
              </Typography>
            </Box>

            <Button
              variant="outlined"
              size="large"
              startIcon={<SettingsRoundedIcon />}
              onClick={openConfigDialog}
              sx={{
                borderRadius: 999,
                px: 3,
                py: 1.25,
              }}
            >
              Configurations Signitic
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {feedback.message ? (
        <Alert severity={feedback.type} sx={{ mb: 3, borderRadius: 3 }}>
          {feedback.message}
        </Alert>
      ) : null}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            title="Configurations"
            value={metrics.configCount}
            subtitle={`${metrics.activeConfigs} actives`}
            icon={<VpnKeyRoundedIcon />}
            tone="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            title="Organisations couvertes"
            value={metrics.activeConfigs}
            subtitle="avec cle API active"
            icon={<ApartmentRoundedIcon />}
            tone="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            title="Utilisateurs visibles"
            value={metrics.userCount}
            subtitle={`${metrics.activeUsers} actifs dans la vue`}
            icon={<GroupsRoundedIcon />}
            tone="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            title="Configs a surveiller"
            value={metrics.configsInError}
            subtitle="erreur ou synchro partielle"
            icon={<WarningAmberRoundedIcon />}
            tone="info"
          />
        </Grid>
      </Grid>

      <Card
        elevation={0}
        sx={{
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'divider',
          mt:10
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ px: 3, pt: 3, pb: 2 }}>
            <Typography variant="h6" fontWeight={700}>
              Utilisateurs Signitic
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Vue consolidee de tous les utilisateurs synchronises, filtrable par organisation via le select global.
            </Typography>
          </Box>
          <Divider />
          <MaterialReactTable table={table} />
        </CardContent>
      </Card>

      <Drawer
        anchor="right"
        open={configDialogOpen}
        onClose={closeConfigDialog}
        PaperProps={{
          sx: {
            width: { xs: '100%', md: '78vw', lg: '1100px' },
            maxWidth: '100%',
          },
        }}
      >
        <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            spacing={1.5}
            alignItems={{ xs: 'flex-start', md: 'center' }}
          >
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Configurations Signitic
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Gere les clefs API, les synchronisations globales ou par organisation, et l etat des configs.
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<SyncRoundedIcon />}
              onClick={handleSync}
              disabled={syncing}
            >
              {syncing ? 'Synchronisation...' : 'Synchroniser toutes les orgs'}
            </Button>
          </Stack>
        </Box>
        <Box sx={{ p: 3, overflowY: 'auto', flex: 1 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={4}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 4,
                  border: '1px solid',
                  borderColor: 'divider',
                  height: '100%',
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight={700} gutterBottom>
                    {selectedConfigId ? 'Modifier une configuration' : 'Nouvelle configuration'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Une cle API par organisation pour pouvoir recuperer les signatures et utilisateurs sur l ensemble du parc.
                  </Typography>

                  <Stack spacing={2}>
                    <TextField
                      select
                      label="Organisation"
                      value={form.organizationId}
                      onChange={handleChange('organizationId')}
                      fullWidth
                      disabled={loadingOrganizations || Boolean(selectedConfigId)}
                      helperText={selectedConfigId ? 'L organisation ne peut pas etre modifiee sur une config existante.' : 'Choisis le client a associer a cette cle.'}
                    >
                      {organizations.map((org) => (
                        <MenuItem key={org._id} value={org._id}>
                          {org.name}
                        </MenuItem>
                      ))}
                    </TextField>

                    <TextField
                      label="Cle API Signitic"
                      value={form.apiKey}
                      onChange={handleChange('apiKey')}
                      fullWidth
                      required
                      type="password"
                      helperText={selectedConfigId ? 'Renseigne une nouvelle cle pour mettre a jour la configuration.' : 'La cle reste stockee en base et n est jamais reaffichee.'}
                    />

                    <TextField
                      label="URL API"
                      value={form.apiUrl}
                      onChange={handleChange('apiUrl')}
                      fullWidth
                    />

                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.enabled}
                          onChange={handleChange('enabled')}
                        />
                      }
                      label={form.enabled ? 'Configuration active' : 'Configuration inactive'}
                    />

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                      <Button
                        variant="contained"
                        onClick={handleSaveConfig}
                        disabled={saving}
                        fullWidth
                      >
                        {saving ? 'Enregistrement...' : selectedConfigId ? 'Mettre a jour' : 'Enregistrer'}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={resetForm}
                        fullWidth
                      >
                        Reinitialiser
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} lg={8}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 4,
                  border: '1px solid',
                  borderColor: 'divider',
                  height: '100%',
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    justifyContent="space-between"
                    spacing={1.5}
                    mb={2}
                  >
                    <Box>
                      <Typography variant="h6" fontWeight={700}>
                        Configurations en place
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Lecture rapide des clefs associees aux organisations et de leur dernier etat de synchro.
                      </Typography>
                    </Box>
                    <Chip
                      label={loadingConfigs ? 'Chargement...' : `${configs.length} configuration${configs.length > 1 ? 's' : ''}`}
                      color="primary"
                      variant="outlined"
                    />
                  </Stack>

                  <Stack spacing={1.5}>
                    {configs.map((config) => {
                      const meta = getStatusMeta(config.lastSyncStatus);
                      return (
                        <Paper
                          key={config._id}
                          elevation={0}
                          sx={{
                            p: 2,
                            borderRadius: 3,
                            border: '1px solid',
                            borderColor: selectedConfigId === config._id ? 'primary.main' : 'divider',
                            bgcolor: selectedConfigId === config._id ? '#f7faff' : 'background.paper',
                          }}
                        >
                          <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            justifyContent="space-between"
                            spacing={2}
                          >
                            <Box>
                              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap mb={1}>
                                <Typography variant="subtitle1" fontWeight={700}>
                                  {config?.Organization?.name || 'Organisation inconnue'}
                                </Typography>
                                <Chip
                                  size="small"
                                  color={config.enabled ? 'success' : 'default'}
                                  label={config.enabled ? 'Active' : 'Inactive'}
                                  variant="outlined"
                                />
                                <Chip
                                  size="small"
                                  color={meta.color}
                                  label={meta.label}
                                  variant="outlined"
                                />
                              </Stack>
                              <Typography variant="body2" color="text.secondary">
                                Cle: {config.apiKeyMasked || 'Masquee'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                URL: {config.apiUrl || 'https://api.signitic.app'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Derniere synchro: {formatDate(config.lastSyncAt)}
                              </Typography>
                              {config.lastSyncMessage ? (
                                <Typography variant="body2" sx={{ mt: 0.75 }}>
                                  {config.lastSyncMessage}
                                </Typography>
                              ) : null}
                            </Box>

                            <Stack direction={{ xs: 'row', md: 'column' }} spacing={1} justifyContent="center">
                              <Button
                                variant="contained"
                                startIcon={<SyncRoundedIcon />}
                                onClick={() => handleSyncConfig(config)}
                                disabled={syncingConfigId === config._id}
                              >
                                {syncingConfigId === config._id ? 'Sync...' : 'Synchroniser'}
                              </Button>
                              <Button
                                variant="outlined"
                                startIcon={<EditRoundedIcon />}
                                onClick={() => handleEditConfig(config)}
                              >
                                Modifier
                              </Button>
                              <Button
                                variant="outlined"
                                color="error"
                                startIcon={<DeleteOutlineRoundedIcon />}
                                onClick={() => handleDeleteConfig(config._id)}
                              >
                                Desactiver
                              </Button>
                            </Stack>
                          </Stack>
                        </Paper>
                      );
                    })}

                    {!loadingConfigs && configs.length === 0 ? (
                      <Paper
                        elevation={0}
                        sx={{
                          p: 3,
                          borderRadius: 3,
                          border: '1px dashed',
                          borderColor: 'divider',
                          textAlign: 'center',
                        }}
                      >
                        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                          Aucune configuration Signitic
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Cree une premiere config par organisation pour pouvoir synchroniser les signatures.
                        </Typography>
                      </Paper>
                    ) : null}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Drawer>
    </Box>
  );
}

export default Signitic;
